import { ContentType, Picture } from '@/@types/common'
import { Post } from '@/@types/post'
import AutoLoadMoreIndicator from '@/components/AutoLoadMoreIndicator'
import ImageGridSection from '@/components/ImageGridSection'
import Page from '@/components/Page'
import { getTopicFeedQueryOptions } from '@/utils/fetchers/topic'
import usePosts from '@/utils/hooks/usePosts'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { useInfiniteQuery } from '@tanstack/react-query'
import { pick } from 'lodash-es'
import { MouseEvent, useMemo } from 'react'
import styles from './TopicHybridPage.module.css'

interface PostPicture extends Picture {
	post: Pick<Post, 'id' | 'user'>
}
export default function TopicHybridPage({ topicId }: { topicId: string }) {
	const {
		data = {
			pages: [],
			pageParams: [],
		},
		fetchNextPage,
		refetch,
		isLoading,
		isFetchingNextPage,
	} = useInfiniteQuery(getTopicFeedQueryOptions(topicId, 'selected'))
	const postIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const posts = usePosts(postIds)
	const pictures = posts.reduce<PostPicture[]>((acc, post) => {
		if (post.type === ContentType.POST && post.pictures && post.user) {
			acc.push(
				...post.pictures.map<PostPicture>((item) => ({
					...item,
					post: pick(post, ['id', 'user']),
				}))
			)
		}
		return acc
	}, [])

	const navigate = useUniversalNavigate()
	const handleItemClick = (picture: PostPicture, index: number, e: MouseEvent) => {
		navigate(
			{
				to: '/u/$username/$type/$id',
				params: {
					username: picture.post.user!.username,
					type: POST_TYPE_TO_ROUTE_MAPPING[ContentType.POST],
					id: picture.post.id,
				},
			},
			e
		)
	}

	return (
		<Page>
			<ImageGridSection
				data={pictures}
				onItemClick={handleItemClick}
				className={styles.pictureGrid}
			/>
			<AutoLoadMoreIndicator
				isLoading={isLoading || isFetchingNextPage}
				onLoadMore={async () => void (await fetchNextPage())}
			/>
		</Page>
	)
}
